'use client'

import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

type Mode = 'light' | 'dark'

type ThemeContextProps = {
    theme: Mode
    setTheme: (mode: Mode) => void
    toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined)

export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Mode>('light')

    useEffect(() => {
        const storedMode = localStorage.getItem('theme') as Mode | null
        const initialMode = storedMode || 'light'
        setTheme(initialMode)
        document.documentElement.setAttribute('data-theme', initialMode)
    }, [])

    const changeTheme = useCallback((newMode: Mode) => {
        setTheme(newMode)
        localStorage.setItem('theme', newMode)
        document.documentElement.setAttribute('data-theme', newMode)
    }, [])

    const toggleTheme = useCallback(() => {
        changeTheme(theme === 'light' ? 'dark' : 'light')
    }, [theme, changeTheme])

    const contextValue = useMemo(() => ({
        theme: theme,
        setTheme: changeTheme,
        toggleTheme
    }), [theme, changeTheme, toggleTheme])

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    )
}

export default ThemeContext